<template>
  <div class="main-collection">
    <garage-hero-banner
      v-if="showingVehicleResults"
      class="main-collection__garage-hero"
      template="results"
      :vehicle="activeVehicle"
    />

    <garage-collection-banner
      v-else
      class="main-collection__garage"
      :namespace="liquid.sectionId"
      :vehicle="activeVehicle"
    />

    <div class="main-collection__grid grid">
      <!-- Filters -->
      <div class="col xs-span l3">
        <filter-and-sort
          v-if="isMobile"
          :active-filters="queryVariables.productFilters"
          :current-sort="currentSort"
          :filters="filters"
          :is-vehicle-finder-collection="isVehicleFinderCollection"
          :price-range="priceRange"
          :showing-vehicle-results="showingVehicleResults"
          :sort-options="sortOptions"
          :total-count="totalCount"
          :vehicle-manufacturer-name="vehicleManufacturerName"
          :vehicle-model-name="vehicleModelName"
          @clear-all="clearAll"
          @clear-filter="clearFilter"
          @handle-sort="handleSort"
          @handle-filter="handleFilter"
          @handle-price-filter="handlePriceFilter"
        />

        <filter-dropdown
          v-else
          class="main-collection__filters"
          :active-filters="queryVariables.productFilters"
          :filters="filters"
          :is-vehicle-finder-collection="isVehicleFinderCollection"
          :price-range="priceRange"
          :showing-vehicle-results="showingVehicleResults"
          :vehicle-manufacturer-name="vehicleManufacturerName"
          :vehicle-model-name="vehicleModelName"
          @clear-all="clearAll"
          @clear-filter="clearFilter"
          @handle-filter="handleFilter"
          @handle-price-filter="handlePriceFilter"
        />
      </div>

      <div class="col xs-span l5-12">
        <div class="main-collection__results-container">
          <p
            class="main-collection__results text-body-xs-regular text-body-s-regular-desktop"
            v-text="numResults"
          />

          <sort-dropdown
            v-if="!isMobile"
            :current-sort="currentSort"
            :sort-options="sortOptions"
            @handle-sort="handleSort"
          />
        </div>

        <ul>
          <li
            v-for="(product, index) of products"
            :key="`product-${index}-${product.id}`"
          >
            <product-card
              :ref="product.handle"
              :product="product"
            />

            <divider-line large />
          </li>
        </ul>

        <main-pagination
          :pagination="pageInfo"
          :results="numResults"
          @load-more="handleLoadMore"
        />

        <div
          v-if="longDescription"
          id="description"
          class="main-collection__long-description"
        >
          <rte-content
            :content="longDescription"
            font-size="s"
          />

          <p class="main-collection__scroll-link text-body-s-underline">
            <a
              class="text-body-s-underline"
              href="#read-more"
              v-text="$string('collection.back_to_top')"
            />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection page component.
 * - Dynamic section.
 *
 * @param {Object} collection - Formatted collection object.
 * @param {Object} liquid - Liquid details.
 * @param {Boolean|String} [longDescription] - Formatted longer description.
 *
 */
import { mapGetters, mapState } from 'vuex'

import DividerLine from '~async/utils/divider-line/divider-line'
import FilterAndSort from '~async/filter-and-sort/filter-and-sort'
import FilterDropdown from '~async/filter-and-sort/filter-dropdown'
import GarageCollectionBanner from '~async/garage-collection-banner/garage-collection-banner'
import GarageHeroBanner from '~async/garage-hero-banner/garage-hero-banner'
import MainPagination from '~async/main-pagination/main-pagination'
import ProductCard from '~global/product-card/product-card'
import RteContent from '~async/rte-content/rte-content'
import SortDropdown from '~async/filter-and-sort/sort-dropdown'

import collectionQuery from '~graphql/collection.gql'
import { formatCollection } from '~/format/format-collection'

import { updateRefElementFocus } from '~/helpers/accessibility'
import { setFiltersFromParams, setParamsFromFilters, toggleObjectFromArray } from '~/helpers/filters'
import { buildTaxonomyMetafieldIdentifiers } from '~/helpers/garage'

export default {
  name: 'MainCollection',

  components: {
    DividerLine,
    FilterAndSort,
    FilterDropdown,
    GarageCollectionBanner,
    GarageHeroBanner,
    MainPagination,
    ProductCard,
    RteContent,
    SortDropdown,
  },

  props: {
    collection: {
      type: Object,
      default: () => ({
        filters: [],
        liquid: true,
        products: [],
        rangeMax: 9999,
        totalCount: 1000,
      }),
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    longDescription: {
      type: [Boolean, String],
      default: false,
    },
    vehicleManufacturerName: {
      type: String,
      default: '',
    },
    vehicleModelName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      currentPage: this.collection.pageInfo.currentPage,
      currentSort: null,
      filters: this.collection.filters,
      // While the actual Shopify maximum is 250 products, they split the
      // response into 3 arrays of 100 products each. To simplify & reduce the
      // size of product queries, limit to at most 100 products
      maxNumProducts: 100,
      pageInfo: this.collection.pageInfo,
      perPage: this.$variable('settings.pagination.products'),
      priceRange: {
        value: null,
        min: 0,
        max: this.collection.rangeMax,
        format: {
          prefix: this.$variable('store.currencySymbol'),
          decimals: 0,
        },
      },
      products: new Array(this.$variable('settings.pagination.products')).fill(false),
      queryVariables: {
        country: this.$variable('store.country'),
        handle: this.collection.handle,
        language: this.$variable('store.language'),
        productFilters: [],
      },
      requestPreviousProducts: false,
      sortOptions: [
        {
          title: this.$string('general.sort_options.default'),
          value: {
            id: 'default',
            sort: 'COLLECTION_DEFAULT',
            reverse: false,
          },
        },
        {
          title: this.$string('general.sort_options.manual'),
          value: {
            id: 'manual',
            sort: 'MANUAL',
            reverse: false,
          },
        },
        {
          title: this.$string('general.sort_options.price_low'),
          value: {
            id: 'price-ascending',
            sort: 'PRICE',
            reverse: false,
          },
        },
        {
          title: this.$string('general.sort_options.price_high'),
          value: {
            id: 'price-descending',
            sort: 'PRICE',
            reverse: true,
          },
        },
        {
          title: this.$string('general.sort_options.newest'),
          value: {
            id: 'created-descending',
            sort: 'CREATED',
            reverse: true,
          },
        },
        {
          title: this.$string('general.sort_options.best_sellers'),
          value: {
            id: 'best-selling',
            sort: 'BEST_SELLING',
            reverse: false,
          },
        },
      ],
      state: 'ready',
      totalCount: this.collection.totalCount,
      urlParameters: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeVehicle: 'garage/getActiveVehicle',
      activeOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if the current fetch is using Liquid-formatted pagination data.
     * - Additional logic is required to handle discrepancies between
     *   Liquid & GQL data.
     * @returns {Boolean}
     */
    isFirstFetch() {
      return this.pageInfo?.liquid
    },

    /**
     * Compute if is results collection.
     * @returns {Boolean}
     */
    isResultsCollection() {
      return this.collection.handle === this.$variable('vehicleFinder.resultsCollection')
    },

    /**
     * Compute if vehicle finder collection (all or results).
     * - Used to determine which hidden filters setting to use.
     * @returns {Boolean}
     */
    isVehicleFinderCollection() {
      return (
        this.collection.handle === this.$variable('vehicleFinder.redirectCollection') ||
        this.collection.handle === this.$variable('vehicleFinder.resultsCollection')
      )
    },

    /**
     * Return the amount of products to query GQL.
     * @returns {Number}
     */
    numberOfProductsToFetch() {
      return this.requestPreviousProducts
        ? Math.min(this.maxNumProducts, this.pageInfo.currentPage * this.perPage)
        : this.perPage
    },

    /**
     * Return the number of results.
     * @returns {String}
     */
    numResults() {
      if (this.state === 'loading') {
        return this.$string('accessibility.loading')
      }

      return this.$string(
        'collection.results_count',
        {
          pluralise: this.totalCount ?? 0,
          replace: {
            products: this.products?.length ?? 0,
            total: this.totalCount ?? 0,
          },
        },
      )
    },

    /**
     * Compute if previous products need to be queried.
     * @returns {Boolean}
     */
    requestPreviousProductsOnLoad() {
      return this.isFirstFetch && this.currentPage > 1
    },

    /**
     * Compute if showing vehicle results.
     * - Based on if all active vehicle's filters are active in the current
     *   collection filters.
     * @returns {Boolean}
     */
    showingVehicleResults() {
      const productFilters = this.queryVariables?.productFilters

      if (
        !this.isResultsCollection ||
        !this.activeVehicle ||
        !productFilters ||
        !productFilters.length
      ) {
        return false
      }

      /**
       * Find if every active vehicle filter is active.
       */
      return this.activeVehicle.filters.every((vehicleFilter) => {
        const key = vehicleFilter.productMetafield?.key
        const namespace = vehicleFilter.productMetafield?.namespace

        const matchingQueryFilter = productFilters?.find((queryFilter) => {
          if (!queryFilter.productMetafield) {
            return false
          }

          return (
            queryFilter.productMetafield.namespace === namespace &&
            queryFilter.productMetafield.key === key
          )
        })

        return matchingQueryFilter
      })
    },

    /**
     * Compute product taxonomy metafield identifiers.
     * @returns {Array}
     */
    taxonomyMetafieldIdentifiers() {
      return buildTaxonomyMetafieldIdentifiers()
    },
  },

  watch: {

    /**
     * Watch active overlay to redirect from results collection if there is no
     * active vehicle.
     */
    activeOverlay() {
      if (
        !this.isResultsCollection ||
        this.activeVehicle ||
        this.activeOverlay
      ) {
        return
      }

      location.href = `${cnvs.routes.normalisedRoot}collections/${this.$variable('vehicleFinder.redirectCollection')}`
    },

    /**
     * Watch active vehicle to update filters or redirect.
     */
    activeVehicle() {
      if (!this.isResultsCollection || !this.activeVehicle) {
        return
      }

      this.queryVariables.productFilters = this.activeVehicle.filters
      this.pageInfo.endCursor = null
      this.queryCollection('filter')
    },

    /**
     * Watch sort object to update sort parameter.
     */
    currentSort() {
      this.setUrlSortParameter(this.currentSort)
    },

    /**
     * Watch current page to update page parameter.
     */
    currentPage() {
      this.setUrlPageParameter()
    },

    /**
     * Watch current filters to update filter parameters.
     */
    'queryVariables.productFilters': {
      handler() {
        this.setUrlFilterParameters()
      },
      deep: true,
    },

    /**
     * Watch showing results to hide title as it's rendered in Liquid.
     */
    showingVehicleResults: {
      handler() {
        const element = document.querySelector('[js-main-collection="header"]')

        if (!this.showingVehicleResults) {
          element.classList.remove(this.$class('visuallyHidden'))
          return
        }

        element.classList.add(this.$class('visuallyHidden'))
      },
      immediate: true,
    },
  },

  mounted() {
    this.initCollection()

    window.addEventListener('popstate', () => {
      this.totalCount = 0
      this.products = []
      this.pageInfo.endCursor = null
      this.getPageValueFromParams()
      this.getSortValueFromParams()
      this.getActiveFiltersFromParams()
      this.queryCollection()
    })
  },

  methods: {

    /**
     * Initialise collection from cached data, or a combination of liquid &
     * GQL data.
     */
    initCollection() {
      this.getSortValueFromParams()
      this.getActiveFiltersFromParams()

      try {
        const cachedCollectionData = JSON.parse(sessionStorage.getItem('collectionData'))

        if (
          !cachedCollectionData ||
          this.$variable('settings.caching.disable') ||
          cachedCollectionData.handle !== this.collection.handle ||
          cachedCollectionData.pageInfo.currentPage !== this.pageInfo.currentPage ||
          cachedCollectionData.totalCount !== this.totalCount ||
          cachedCollectionData.urlParameters !== this.urlParameters
        ) {
          this.loadAndSaveCollectionData()
          return
        }

        this.resetProductsAndFilters(cachedCollectionData)

      } catch (error) {
        this.loadAndSaveCollectionData()

        cnvs.ReportError('Failed to retrieve collection data from session storage', error)
      }
    },

    /**
     * Reset variables related products and
     * filters from cached collection data.
     * @param {Object} data - cached collection data
     */
    resetProductsAndFilters(data) {
      this.products = data.products
      this.totalCount = data.totalCount
      this.updatePriceRange()
    },

    /**
     * Set whether previous products need to be queried, then query products.
     */
    async loadAndSaveCollectionData() {
      this.setRequestPreviousProducts(this.requestPreviousProductsOnLoad)
      await this.queryCollection()
    },

    /**
     * Set if previous products should be queried.
     * @param {Boolean} requestPrevious - Query previous products
     */
    setRequestPreviousProducts(requestPrevious) {
      this.requestPreviousProducts = requestPrevious
    },

    /**
     * Cache collection handle and products.
     */
    cacheCollectionProducts() {
      try {
        const collectionData = {
          handle: this.collection.handle,
          pageInfo: this.pageInfo,
          products: this.products,
          totalCount: this.totalCount,
          urlParameters: this.urlParameters,
        }

        sessionStorage.setItem('collectionData', JSON.stringify(collectionData))

      } catch (error) {
        cnvs.ReportError('Failed to save collection to session storage', error)
      }
    },

    /**
     * Query collection from GraphQL.
     * @param {String} requestType - String mapping to 'load-more',
     * 'filter' to manage functions limited to one type of query.
     */
    async queryCollection(requestType) {
      const queryRange = {
        first: this.numberOfProductsToFetch,
      }

      if (!this.requestPreviousProducts) {
        queryRange.after = this.pageInfo.endCursor
      }

      try {
        this.state = 'loading'

        const response = await cnvs.Query({
          query: collectionQuery,
          variables: {
            ...queryRange,
            ...this.queryVariables,
            reverse: this.currentSort?.reverse,
            sort: this.currentSort?.sort,
            taxonomyMetafieldIdentifiers: this.taxonomyMetafieldIdentifiers,
          },
        })

        this.handleResponse(response, requestType)

      } catch (error) {
        cnvs.ReportError('Failed to load collection', error)
      }
    },

    /**
     * Handle query response.
     * - Updates local `collection` & `pagination` objects, and local
     *   `products` array.
     * - Handles page number increase.
     * @param {Object} response - GQL query response.
     * @param {String} requestType - String mapping to 'load-more',
     * 'filter' to manage functions limited to one type of query.
     */
    handleResponse(response, requestType) {

      /**
       * If requestType is 'filter' reset current page to 1.
       * Else if 'requestPreviousProducts' is false, pass increased current
       * page to collection response so that the correct data is cached.
       */
      const isFilterOrSort = requestType === 'filter'

      if (isFilterOrSort) {
        this.currentPage = 1
      }

      /**
       * If we hit the maximum number of products we can request from Shopify,
       * recalculate the current page number.
       */
      if (this.numberOfProductsToFetch === this.maxNumProducts) {
        this.currentPage = Math.ceil(this.maxNumProducts / this.perPage)
      }

      const collection = formatCollection(
        response,
        {
          currentOffset: this.products.length,
          currentPage: this.currentPage,
        },
      )

      if (!collection) {
        cnvs.ReportError('Failed to load collection.')
        return
      }

      this.totalCount = collection.pageInfo.totalCount
      this.filters = collection?.filters
      this.updatePriceRange()

      if (isFilterOrSort || this.requestPreviousProducts || this.isFirstFetch) {
        this.products = collection.products
        this.pageInfo = collection.pageInfo
        this.state = 'ready'
        this.setRequestPreviousProducts(false)
        this.cacheCollectionProducts()

        return
      }

      /**
       * Prevents duplicating products if there are fewer products returned
       * compared to the number of products per page.
       */
      const newProducts = collection.products.length > this.perPage
        ? collection.products.slice(this.pageInfo.currentOffset + this.perPage)
        : collection.products

      this.products.push(...newProducts)

      this.pageInfo = collection.pageInfo
      this.state = 'ready'

      if (requestType === 'load-more') {
        this.$nextTick().then(() => {
          updateRefElementFocus(this.$refs, newProducts[0]?.handle)
        })
      }

      this.cacheCollectionProducts()
    },

    /**
     * Handle load more button press.
     */
    handleLoadMore() {
      this.currentPage += 1
      this.queryCollection('load-more')
    },

    /**
     * Get page value from URL params on page load
     */
    getPageValueFromParams() {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)
      const page = params.get('page')

      if (!page) {
        this.currentPage = 1
        return
      }

      this.currentPage = Number(page)
    },

    /**
     * Get sort value from URL params on page load
     */
    getSortValueFromParams() {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)
      const sort = params.get('sort_by')

      if (!sort) {
        this.currentSort = null
        return
      }

      this.currentSort =
        this.sortOptions.find((option) => option.value.id === sort)?.value ||
        this.currentSort
    },

    /**
     * Get filter values from URL params on page load
     */
    getActiveFiltersFromParams() {
      const currentUrl = new URL(window.location)
      const params = currentUrl.searchParams
      const filterParams = []

      params.forEach((value, key) => {
        if (key.startsWith('filter.') && value) {
          filterParams.push({ key, value })
        }
      })

      if (!filterParams.length) {
        return
      }

      this.urlParameters = params.toString()
      this.queryVariables.productFilters = setFiltersFromParams(filterParams)
    },

    /**
     * Set URL page parameter.
     */
    setUrlPageParameter() {
      const currentUrl = new URL(window.location)

      if (!this.currentPage || this.currentPage <= 1) {
        currentUrl.searchParams.delete('page')
        this.updateWindowHistory(currentUrl)
        return
      }

      currentUrl.searchParams.set('page', this.currentPage)
      this.updateWindowHistory(currentUrl)
    },

    /**
     * Set URL sort param with current sort option
     * @param {Object} sort - Object containing current sort values
     */
    setUrlSortParameter(sort) {
      const currentUrl = new URL(window.location)

      if (!sort) {
        currentUrl.searchParams.delete('sort_by')
        this.updateWindowHistory(currentUrl)
        return
      }

      currentUrl.searchParams.set('sort_by', sort.id)
      this.updateWindowHistory(currentUrl)
    },

    /**
     * Set URL filter params with active filters
     */
    setUrlFilterParameters() {
      const productFilters = this.queryVariables.productFilters
      const currentUrl = new URL(window.location)
      let params = currentUrl.searchParams
      const filteredParams = Array.from(params.entries()).filter(([key]) => {
        return !key.includes('filter.')
      })

      currentUrl.search = new URLSearchParams(filteredParams).toString()
      params = currentUrl.searchParams

      if (!productFilters.length) {
        this.updateWindowHistory(currentUrl)
        return
      }

      setParamsFromFilters(params, productFilters)

      this.updateWindowHistory(currentUrl)
    },

    /**
     * Update window history with new url.
     * @param {Object} currentUrl - new Url
     */
    updateWindowHistory(currentUrl) {
      const url = new URL(currentUrl)
      this.urlParameters = url.searchParams.toString()
      window.history.pushState({}, null, currentUrl)
    },

    /**
     * Update price range from filters.
     */
    updatePriceRange() {
      const priceFilter = this.filters.find(
        (filter) => filter.type === 'PRICE_RANGE',
      )

      if (!priceFilter) {
        this.priceRange = {}
        return
      }

      const priceInput = this.filters.find(
        (item) => item.id === 'filter.v.price',
      ).values[0].input

      if (!priceInput) {
        return
      }

      this.priceRange.value = priceInput.price
    },

    /**
     * Reset filters and close filter drawer.
     * @param {Array} hiddenActiveFilters - Hidden active filters.
     */
    clearAll(hiddenActiveFilters) {
      if (!this.queryVariables.productFilters.length) {
        return
      }

      this.queryVariables.productFilters = hiddenActiveFilters
      this.pageInfo.endCursor = null
      this.queryCollection('filter')
    },

    /**
     * Handle clear filter.
     * @param {String} filter - Filter group.
     */
    clearFilter(filter) {
      filter.values.forEach((value) => {
        const index = this.queryVariables.productFilters.findIndex((input) => {
          return JSON.stringify(value.input) === JSON.stringify(input)
        })

        if (index === -1) {
          return
        }

        this.queryVariables.productFilters.splice(index, 1)
      })

      this.pageInfo.endCursor = null
      this.queryCollection('filter')
    },

    /**
     * Handle filter change.
     * @param {Object} filter - Filter object.
     */
    handleFilter(filter) {
      if (filter.value === 0) {
        return
      }

      this.pageInfo.endCursor = null

      toggleObjectFromArray(
        this.queryVariables.productFilters,
        filter.input,
      )

      this.queryCollection('filter')
    },

    /**
     * Handle price filter change.
     * @param {Array} value - Price range.
     */
    handlePriceFilter(value) {
      this.pageInfo.endCursor = null

      if (value === this.priceRange.value) {
        const priceIndex = this.queryVariables.productFilters.findIndex(
          (filter) => filter.price,
        )

        this.queryVariables.productFilters.splice(priceIndex, 1)
        return
      }

      const { min, max } = value

      const priceFilter = this.queryVariables.productFilters.find(
        (filter) => filter.price,
      )

      if (priceFilter) {
        priceFilter.price = {
          min,
          max,
        }
      } else {
        this.queryVariables.productFilters.push({
          price: {
            min,
            max,
          },
        })
      }

      this.queryCollection('filter')
    },

    /**
     * Handle sort change.
     * @param {Object} currentSort - Sort option object.
     */
    handleSort(currentSort) {
      this.currentSort = currentSort
      this.pageInfo.endCursor = null
      this.queryCollection('filter')
    },
  },
}
</script>

<style lang="scss">
@import '@/components/pagination';
@import './main-collection';
@import '~async/main-pagination/main-pagination';
</style>
